import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { appPath, buildApps, findBuildApp } from '@/seo/catalog';
import { SiteMeta } from '@/components/site/SiteMeta';
import {
  appBadgeLabel,
  appPriceLabel,
  appUsageHint,
  fetchPublicApps,
  sortApps,
  type PublicApp,
  type PublicAppTier,
} from '@/content/apps';
import { trialHref } from '@/content/plans';
import {
  Boxes,
  CalendarDays,
  Car,
  ClipboardList,
  Code2,
  CreditCard,
  FileText,
  FlaskConical,
  Globe,
  HeartHandshake,
  MessageCircle,
  MonitorPlay,
  PackageSearch,
  ShoppingBag,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  Ticket,
  Wallet,
  Wrench,
  type LucideIcon,
} from 'lucide-react';

const badgeClass: Record<PublicAppTier, string> = {
  included: 'inc',
  addon: 'add',
  soon: 'soon',
};

const iconByName: Record<string, LucideIcon> = {
  Stethoscope,
  Wrench,
  Car,
  FileText,
  CreditCard,
  Wallet,
  CalendarDays,
  Sparkles,
  Code2,
  MessageCircle,
  PackageSearch,
  ShoppingBag,
  ShieldCheck,
  ClipboardList,
  HeartHandshake,
  Globe,
  MonitorPlay,
  Ticket,
  FlaskConical,
  Boxes,
};

function resolveIcon(name: string | null): LucideIcon {
  if (name && iconByName[name]) return iconByName[name];
  return Boxes;
}

/**
 * Card vira link só quando a página do app existe neste build. App que entrou no
 * catálogo depois do último deploy aparece no card (lista ao vivo), mas a página
 * própria só nasce no próximo build; até lá, link levaria para uma 404.
 */
function CardShell({ slug, children }: { slug: string; children: ReactNode }) {
  return findBuildApp(slug) ? (
    <Link className="app-card rev" to={appPath(slug)}>
      {children}
    </Link>
  ) : (
    <div className="app-card rev">{children}</div>
  );
}

function AppCard({ app }: { app: PublicApp }) {
  const Icon = resolveIcon(app.icon);
  const price = appPriceLabel(app);
  const hint = appUsageHint(app);
  const soon = app.tier === 'soon' || app.availability === 'coming_soon';

  return (
    <CardShell slug={app.slug}>
      <span className={`app-badge ${badgeClass[app.tier]}`}>{appBadgeLabel(app.tier)}</span>
      <div className="ic">
        <Icon strokeWidth={2} />
      </div>
      <div className="nm">{app.name}</div>
      <div className="ds">{app.description ?? ''}</div>
      <div className="app-price" style={soon ? { color: 'var(--muted)' } : undefined}>
        {price ?? (soon ? 'Em breve' : 'Sob consulta')}
        {hint ? <div style={{ fontWeight: 500, color: 'var(--muted)', marginTop: 4 }}>{hint}</div> : null}
      </div>
      {findBuildApp(app.slug) ? (
        <span className="app-more">
          Ver detalhes <span aria-hidden>→</span>
        </span>
      ) : null}
    </CardShell>
  );
}

function AppsGrid() {
  // Nasce com o catálogo do build: é o que vai no HTML gerado e o que o React
  // encontra ao assumir a página. A busca ao vivo só atualiza por cima, e se a
  // API falhar o visitante continua vendo o catálogo em vez de uma mensagem de erro.
  const [apps, setApps] = useState<PublicApp[]>(buildApps);

  useEffect(() => {
    let cancelled = false;
    fetchPublicApps()
      .then((data) => {
        if (!cancelled && data.length > 0) setApps(sortApps(data));
      })
      .catch(() => {
        /* segue com o catálogo do build */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (apps.length === 0) {
    return (
      <div style={{ textAlign: 'center', marginTop: 24 }}>
        <p className="note" style={{ fontStyle: 'normal' }}>
          Não foi possível carregar o catálogo de apps agora.
        </p>
        <button type="button" className="btn btn-ghost" style={{ marginTop: 12 }} onClick={() => window.location.reload()}>
          Tentar de novo
        </button>
      </div>
    );
  }

  return (
    <div className="apps-grid">
      {apps.map((app) => (
        <AppCard key={app.slug} app={app} />
      ))}
    </div>
  );
}

function ShotFrame({ children }: { children: ReactNode }) {
  return (
    <div className="rev hero-win-wrap frame-wide">
      <div className="win">
        <div className="win-bar">
          <div className="win-dots">
            <i />
            <i />
            <i />
          </div>
          <div className="win-url">
            <span>app.operax.digital/apps</span>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

export function AppsPage() {
  return (
    <>
      <SiteMeta
        title={'Portal de Apps - Operax'}
        description={
          'Ative agenda, estoque, Pix, NFS-e e outros módulos quando a operação pedir. Catálogo oficial com preços atualizados.'
        }
        path={'/apps'}
      />

      <section className="page-hero">
        <div className="hero-grid-bg" />
        <div className="shell page-hero-in rev">
          <h1>O que a operação precisa, quando precisar.</h1>
          <p className="lead">
            O Portal de Apps é um catálogo de módulos. Cada app liga menus, telas e automações do seu setor. Hoje
            são {buildApps.length} apps, e o catálogo cresce junto com a plataforma. Sem migração, sem trocar de
            sistema, sem mexer no núcleo. Os preços abaixo vêm do catálogo oficial.
          </p>
        </div>
      </section>

      <section className="pad">
        <div className="shell">
          <ShotFrame>
            <picture>
              <source srcSet="/screens/apps.avif" type="image/avif" />
              <source srcSet="/screens/apps.webp" type="image/webp" />
              <img
                className="shot"
                src="/screens/apps.png"
                alt="Portal de apps do Operax com módulos ativáveis por empresa"
                width={1440}
                height={863}
                loading="lazy"
                decoding="async"
              />
            </picture>
          </ShotFrame>
          <AppsGrid />
        </div>
      </section>

      {/* Corte limpo branco→navy; band continua no footer (#070d1a) — sem to/from-navy */}
      <section className="navyband navyband--into-foot" style={{ padding: '48px 0 100px' }}>
        <div className="shell">
          <div className="rev" style={{ textAlign: 'center', maxWidth: 620, margin: '0 auto' }}>
            <div className="eyebrow center">Cresce com você</div>
            <h2 className="h2">Comece enxuto. Cresça sem refazer o sistema.</h2>
            <p className="lead" style={{ marginTop: 16 }}>
              Precisou de mais gente ou de outro módulo? Ativa no painel e usa. Sem projeto novo a cada passo.
            </p>
            <div className="hero-cta" style={{ justifyContent: 'center', marginTop: 28 }}>
              <a className="btn btn-white" href={trialHref()}>
                Assinar agora{' '}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4}>
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </a>
              <a className="btn btn-onnavy" href="/precos">
                Ver planos
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
